/**
 * Shared rendering utilities
 *
 * Common helper functions used by all rendering pipelines:
 * - ComponentRenderer (page builder editor)
 * - generatePreviewHTML (iframe preview)
 * - LayoutComponent (public pages)
 */

/**
 * Alignment value mapping for backward compatibility.
 * Maps old values (start, end) to proper CSS flex values.
 * TODO: Remove once all pages are migrated to new values.
 */
export const ALIGNMENT_VALUE_MAP = {
  'start': 'flex-start',
  'end': 'flex-end',
  'flex-start': 'flex-start',
  'flex-end': 'flex-end',
  'center': 'center',
  'stretch': 'stretch',
  'baseline': 'baseline',
};

/**
 * Column layout presets using 12-column grid system
 */
export const COLUMN_LAYOUT_PRESETS = {
  'equal': [],
  '1-2': [4, 8],
  '2-1': [8, 4],
  '1-3': [3, 9],
  '3-1': [9, 3],
  '1-1-1': [4, 4, 4],
  '1-2-1': [3, 6, 3],
  'stacked': [12],
  'custom': [],
};

/**
 * Normalize alignment values for backward compatibility.
 * Maps old values (start, end) to proper CSS flex values (flex-start, flex-end).
 * @param {string} value - Alignment value
 * @returns {string} Normalized alignment value
 */
export function normalizeAlignmentValue(value) {
  if (!value) return value;
  return ALIGNMENT_VALUE_MAP[value] || value;
}

/**
 * Resolve a link value to a URL string.
 * Handles both plain string URLs and internal page link objects.
 * @param {string|Object} linkValue - URL string or { type: "internal", pageId: string }
 * @returns {string} Resolved URL string
 */
export function resolveLinkUrl(linkValue) {
  if (!linkValue) return '';
  if (typeof linkValue === 'string') return linkValue;
  if (typeof linkValue === 'object' && linkValue.type === 'internal' && linkValue.pageId) {
    return `/pages/${linkValue.pageId}`;
  }
  return '';
}

/**
 * Check if a link value is non-empty (works for both string and object formats)
 * @param {string|Object} linkValue
 * @returns {boolean}
 */
export function hasLinkUrl(linkValue) {
  if (!linkValue) return false;
  if (typeof linkValue === 'string') return linkValue !== '' && linkValue !== '#';
  if (typeof linkValue === 'object' && linkValue.type === 'internal') return !!linkValue.pageId;
  return false;
}

/**
 * Convert a JavaScript style object to CSS string.
 * Converts camelCase properties to kebab-case.
 * @param {Object} styleObj - JavaScript style object with camelCase properties
 * @returns {string} CSS string with kebab-case properties
 */
export function styleObjectToCSS(styleObj) {
  if (!styleObj || typeof styleObj !== 'object') return '';

  return Object.entries(styleObj)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => {
      const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      return `${cssKey}: ${value};`;
    })
    .join(' ');
}

/**
 * Sanitize custom CSS to remove potentially dangerous patterns
 * @param {string} css - Raw CSS string
 * @returns {string} Sanitized CSS string
 */
export function sanitizeCSS(css) {
  if (!css || !css.trim()) return '';
  return css
    .replace(/javascript:/gi, '')
    .replace(/expression\s*\(/gi, '')
    .replace(/<\s*script/gi, '')
    .replace(/@import/gi, '');
}

/**
 * Get padding/margin with unit support (backward compatible with number format)
 * @param {number|Object} spacingProp - Number or { top, bottom, left, right, unit }
 * @param {number} defaultValue - Default value for each side
 * @returns {Object} { top, bottom, left, right, unit }
 */
export function getSpacingWithUnit(spacingProp, defaultValue = 0) {
  if (typeof spacingProp === 'number') {
    return {
      top: spacingProp,
      bottom: spacingProp,
      left: spacingProp,
      right: spacingProp,
      unit: 'px',
    };
  } else if (typeof spacingProp === 'object' && spacingProp !== null) {
    return {
      top: spacingProp.top ?? defaultValue,
      bottom: spacingProp.bottom ?? defaultValue,
      left: spacingProp.left ?? defaultValue,
      right: spacingProp.right ?? defaultValue,
      unit: spacingProp.unit || 'px',
    };
  }
  return {
    top: defaultValue,
    bottom: defaultValue,
    left: defaultValue,
    right: defaultValue,
    unit: 'px',
  };
}
